import {
  type IncomingCall,
  useIncomingCallStore,
} from "@notifications/incomingCallStore";
import { useSoftphoneStore } from "@phone/softphone";
import { useEffect, useState } from "react";
import { create } from "zustand";

/**
 * The inbound call this staff member is carrying, or the last one they carried
 * once it hangs up: the call bubble outlives the call card, which slips away
 * before a countdown or calendar write finishes. `call` is set only while the
 * call is live here.
 */
export const useCornerCall = (): {
  callUuid: string | null;
  call: IncomingCall | null;
  clientName: string | null;
  ended: boolean;
} => {
  const answeredCallUuid = useSoftphoneStore((state) => state.answeredCallUuid);
  const call = useIncomingCallStore((state) =>
    state.calls.find((item) => item.callUuid === answeredCallUuid),
  );
  const live = call && call.origin === "inbound" && !call.settled ? call : null;
  const name = call?.clientName ?? call?.callerName ?? null;
  const [recent, setRecent] = useState<{
    callUuid: string;
    clientName: string | null;
  } | null>(null);
  if (
    live !== null &&
    (live.callUuid !== recent?.callUuid || name !== recent.clientName)
  )
    setRecent({ callUuid: live.callUuid, clientName: name });
  return {
    callUuid: live?.callUuid ?? recent?.callUuid ?? null,
    call: live,
    clientName: recent?.clientName ?? null,
    ended: live === null,
  };
};

const useCallBubbleHosts = create<{ hosts: number }>(() => ({ hosts: 0 }));

/** Mounted by the call bubble: while one is mounted it carries the answered inbound call's controls. */
export const useHostCallBubble = () =>
  useEffect(() => {
    useCallBubbleHosts.setState((s) => ({ hosts: s.hosts + 1 }));
    return () => useCallBubbleHosts.setState((s) => ({ hosts: s.hosts - 1 }));
  }, []);

/** True while a call bubble is mounted, so the corner card leaves the answered inbound call to it. */
export const useCallBubbleHosted = () => useCallBubbleHosts((s) => s.hosts > 0);
